import React, { useState, useEffect, useMemo } from 'react';
import './Admin.css';

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
function getToken() {
  try {
    const s = sessionStorage.getItem('supabase-auth-token');
    return s ? (JSON.parse(s).access_token ?? process.env.REACT_APP_SUPABASE_ANON_KEY) : process.env.REACT_APP_SUPABASE_ANON_KEY;
  } catch { return process.env.REACT_APP_SUPABASE_ANON_KEY; }
}

function apiFetch(path) {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return fetch(`${url}/rest/v1/${path}`, {
    headers: { apikey: key, Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  });
}


const ROLE_COLORS = {
  super_admin:   '#7c3aed',
  platform_admin:'#006B54',
  system_admin:  '#d97706',
  ceo:           '#1d4ed8',
  chro:          '#0891b2',
  function_head: '#059669',
  individual:    '#64748b',
  user:          '#64748b',
};

const ROLE_LABELS = {
  super_admin:    'Super Admin',
  platform_admin: 'Platform Admin',
  system_admin:   'System Admin',
  ceo:            'CEO',
  chro:           'CHRO',
  function_head:  'Function Head',
  individual:     'Individual',
  user:           'User',
};

/* ──────────────────────────────────────────────
   Simple bar chart (pure CSS)
────────────────────────────────────────────── */
function BarChart({ data, maxValue }) {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120, padding: '0 4px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: 4 }}>
          <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>{d.value}</span>
          <div
            style={{
              width: '100%',
              height: `${Math.max((d.value / max) * 100, 4)}%`,
              background: d.color ?? 'linear-gradient(180deg, #006B54 0%, #00795f 100%)',
              borderRadius: '4px 4px 0 0',
              transition: 'height 0.5s ease',
            }}
          />
          <span style={{ fontSize: '0.68rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {d.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
const UsageAnalytics = () => {
  const [profiles, setProfiles]         = useState([]);
  const [orgs, setOrgs]                 = useState([]);
  const [evaluations, setEvaluations]   = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pRes, oRes, eRes] = await Promise.all([
          apiFetch('profiles?select=id,role,organization_id,created_at&order=created_at.asc'),
          apiFetch('organizations?select=id,name,plan,created_at'),
          apiFetch('evaluations?select=id,created_at&order=created_at.desc&limit=500'),
        ]);
        setProfiles(pRes.ok  ? await pRes.json()  : []);
        setOrgs(    oRes.ok  ? await oRes.json()  : []);
        setEvaluations(eRes.ok ? await eRes.json() : []);
      } catch {
        /* Silently degrade — show what we can */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ── Derived stats ── */
  const stats = useMemo(() => {
    const now         = new Date();
    const thirtyAgo   = new Date(now - 30 * 24 * 60 * 60 * 1000);
    const sevenAgo    = new Date(now - 7 * 24 * 60 * 60 * 1000);

    const recentUsers = profiles.filter(p => new Date(p.created_at) >= thirtyAgo).length;
    const recentEvals = evaluations.filter(e => new Date(e.created_at) >= sevenAgo).length;

    const roleBreakdown = Object.entries(
      profiles.reduce((acc, p) => { acc[p.role ?? 'unknown'] = (acc[p.role ?? 'unknown'] ?? 0) + 1; return acc; }, {})
    ).map(([role, count]) => ({ role, count, label: ROLE_LABELS[role] ?? role, color: ROLE_COLORS[role] ?? '#94a3b8' }))
      .sort((a, b) => b.count - a.count);

    /* Users joined per month (last 6 months) */
    const monthlyJoins = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      return {
        label: d.toLocaleString('default', { month: 'short' }),
        value: profiles.filter(p => {
          const c = new Date(p.created_at);
          return c >= d && c < end;
        }).length,
      };
    });

    /* Tenants by plan */
    const planBreakdown = [
      { label: 'Starter',      value: orgs.filter(o => o.plan === 'starter').length,      color: '#15803d' },
      { label: 'Professional', value: orgs.filter(o => o.plan === 'professional').length, color: '#1d4ed8' },
      { label: 'Enterprise',   value: orgs.filter(o => o.plan === 'enterprise').length,   color: '#7c3aed' },
      { label: 'No plan',      value: orgs.filter(o => !o.plan).length,                   color: '#94a3b8' },
    ].filter(p => p.value > 0);

    return { recentUsers, recentEvals, roleBreakdown, monthlyJoins, planBreakdown };
  }, [profiles, orgs, evaluations]);

  /* Top tenants by user count */
  const topTenants = useMemo(() => {
    const counts = profiles.reduce((acc, p) => {
      if (!p.organization_id) return acc;
      acc[p.organization_id] = (acc[p.organization_id] ?? 0) + 1;
      return acc;
    }, {});
    const orgMap = Object.fromEntries(orgs.map(o => [o.id, o.name]));
    return Object.entries(counts)
      .map(([id, count]) => ({ id, name: orgMap[id] ?? id, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [profiles, orgs]);

  return (
    <div className="adm-container">

      <div className="adm-page-header">
        <h1 className="adm-page-title"><i className="bi bi-graph-up" />Usage Analytics</h1>
        <p className="adm-page-subtitle">Platform-wide usage, adoption metrics, and user growth across all tenant organizations.</p>
      </div>
      <hr className="adm-divider" />

      <div className="adm-stats-row">
        {[
          { label: 'Total Users',       value: profiles.length,      icon: 'bi-people',         color: '#006B54' },
          { label: 'New Users (30d)',    value: stats.recentUsers,    icon: 'bi-person-plus',    color: '#0891b2' },
          { label: 'Total Tenants',     value: orgs.length,          icon: 'bi-buildings',       color: '#1d4ed8' },
          { label: 'Evaluations (7d)',  value: stats.recentEvals,    icon: 'bi-clipboard-check', color: '#059669' },
        ].map(s => (
          <div key={s.label} className="adm-stat-card" style={{ borderTopColor: s.color }}>
            <div className="adm-stat-icon" style={{ background: s.color + '18', color: s.color }}>
              <i className={`bi ${s.icon}`} />
            </div>
            <div className="adm-stat-body">
              <div className="adm-stat-value">{loading ? '—' : s.value}</div>
              <div className="adm-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="adm-grid-2" style={{ marginBottom: 20 }}>

        {/* User growth */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">New Users — Last 6 Months</span>
          </div>
          <div className="adm-card-body">
            {loading ? (
              <div className="adm-empty" style={{ padding: 30 }}><div className="adm-spinner" /></div>
            ) : (
              <BarChart data={stats.monthlyJoins} />
            )}
          </div>
        </div>

        {/* Role breakdown */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">Users by Role</span>
          </div>
          <div className="adm-card-body--flush">
            {loading ? (
              <div className="adm-empty" style={{ padding: 30 }}><div className="adm-spinner" /></div>
            ) : stats.roleBreakdown.length === 0 ? (
              <div className="adm-empty" style={{ padding: 30 }}>
                <i className="bi bi-person-badge" />
                <span className="adm-empty-text">No role data yet.</span>
              </div>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr><th>Role</th><th>Users</th><th>Share</th></tr>
                </thead>
                <tbody>
                  {stats.roleBreakdown.map(r => (
                    <tr key={r.role}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: r.color, display: 'inline-block', flexShrink: 0 }} />
                          {r.label}
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{r.count}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 80, height: 6, background: '#e8edf2', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.round((r.count / profiles.length) * 100)}%`, height: '100%', background: r.color, borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {profiles.length ? `${Math.round((r.count / profiles.length) * 100)}%` : '0%'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="adm-grid-2">

        {/* Top tenants */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">Top Tenants by User Count</span>
          </div>
          <div className="adm-card-body--flush">
            {loading ? (
              <div className="adm-empty" style={{ padding: 30 }}><div className="adm-spinner" /></div>
            ) : topTenants.length === 0 ? (
              <div className="adm-empty" style={{ padding: 30 }}>
                <i className="bi bi-buildings" />
                <span className="adm-empty-text">No tenant data yet.</span>
              </div>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr><th>#</th><th>Organization</th><th>Users</th></tr>
                </thead>
                <tbody>
                  {topTenants.map((t, i) => (
                    <tr key={t.id}>
                      <td style={{ color: '#94a3b8', fontWeight: 600, width: 36 }}>{i + 1}</td>
                      <td style={{ fontWeight: 500 }}>{t.name}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 70, height: 6, background: '#e8edf2', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.round((t.count / topTenants[0].count) * 100)}%`, height: '100%', background: '#006B54', borderRadius: 4 }} />
                          </div>
                          <span style={{ fontWeight: 600 }}>{t.count}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Plan adoption */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">Plan Adoption</span>
          </div>
          <div className="adm-card-body">
            {loading ? (
              <div className="adm-empty" style={{ padding: 30 }}><div className="adm-spinner" /></div>
            ) : stats.planBreakdown.length === 0 ? (
              <div className="adm-empty" style={{ padding: 30 }}>
                <i className="bi bi-pie-chart" />
                <span className="adm-empty-text">No plan data yet.</span>
              </div>
            ) : (
              <>
                <BarChart data={stats.planBreakdown} />
                <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {stats.planBreakdown.map(p => (
                    <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#4a5a54' }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: p.color, display: 'inline-block' }} />
                      {p.label}: <strong>{p.value}</strong>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default UsageAnalytics;
